<script setup lang="ts">
import { Breadcrumb, MetaTitle, ContentResults } from "@/types/Facet";

const route = useRoute();
const config = useRuntimeConfig();
const { $fireBangApiDomain, $log } = useNuxtApp();

// 記事情報取得
const { data: fetchContdata } = await useFetch(`${$fireBangApiDomain}/api/facet/cont/${route.params.id}`, {
  key:'contData',
  transform: (data: ContentResults) => {
    const results = data ? data.results : null;
    return results;
  },
});

const metaTitle: MetaTitle = useState("metaTitle");

metaTitle.value = fetchContdata.value?.title;

if(!fetchContdata.value) {
  const error = new Error('記事が見つかりませんでした');
  $log.error(error);
  throw error;
}

useHead({
  bodyAttrs: {
    class: `${fetchContdata.value?.urlWord}`,
  },
});

useSeoMeta({
  title: `${fetchContdata.value?.title}`,
  keywords: `${fetchContdata.value?.keywords}`,
  description: `${fetchContdata.value?.description}`,
  ogTitle: `${fetchContdata.value?.title}`,
  ogDescription: `${fetchContdata.value?.description}`,
  ogUrl: `${config.public.fireBangDomainName}/cont/${route.params.id}/`,
  ogImage: `${config.public.fireBangDomainName}/_contents/cms/${fetchContdata.value?.image?.tableName}/${fetchContdata.value?.id}/image/${fetchContdata.value?.image?.fileName}`,
});


// /cont/ディレクトリのみパンくずと構造化データの作りが異なるため、composables(useBreadcrumbsAndJsonld)を使用していない
const breadcrumbs: Array<Breadcrumb> = [
  {
    url: `${config.public.fireBangDomainName}`,
    text: 'bang! 火災保険トップ'
  },
  {
    url:
      `${config.public.fireBangDomainName}` +
      `/cate/` +
      fetchContdata.value?.categoryId +
      `/`,
    text: `${fetchContdata.value?.categoryName}`
  },
  {
    text: `${fetchContdata.value?.headline}`
  }
]

const items = breadcrumbs.map((item: Breadcrumb, index: number) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.text,
    item: item.url,
}));

useJsonld(() => ({
  "@context": "https://schema.org",
  "@type": "Thing",
  BreadcrumbList: {
    "@type": "BreadcrumbList",
    itemListElement: items,
  },
  Article: {
    '@type': 'Article',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${config.public.fireBangDomainName}/cont/${
        fetchContdata.value?.urlWord
      }/`
    },
    headline: fetchContdata.value?.title,
    image: {
      '@type': 'ImageObject',
      url: `${config.public.fireBangDomainName}/_contents/cms/${
        fetchContdata.value?.image?.tableName
      }/${fetchContdata.value?.id}/image/${
        fetchContdata.value?.image?.fileName
      }`,
      height: 640,
      width: 427
    },
    datePublished: fetchContdata.value?.publishedAt,
    dateModified: fetchContdata.value?.modifiedAt,
    publisher: {
      '@type': 'Organization',
      name: '株式会社ウェブクルー',
      logo: {
        '@type': 'ImageObject',
        url:
          'https://img.phoenix.webcrew.co.jp/12200/files/images/header_logo.png',
        width: 116,
        height: 42
      }
    }
  }
}));

</script>

<template>
  <div>
    <main class="cont" role="main">
      <div class="breadcrumbs">
          <span v-for="(item, index) in breadcrumbs" :key="item.url">
            <span v-if="index !== breadcrumbs.length - 1">
              <a v-if="item.url" :href="item.url">{{ item.text }}</a>
              <span v-else>{{ item.text }}</span>
              ＞
            </span>
            <span v-else>{{ item.text }}</span>
          </span>
        </div>
      <article>
        <h1>{{ fetchContdata?.headline }}</h1>
        <div class="update">
          <span
            >更新日：<time :datetime="fetchContdata?.modifiedAt.toString()">{{
              $dayjs(fetchContdata?.modifiedAt).format("YYYY年MM月DD日")
            }}</time></span
          >
          <span
            >公開日：<time :datetime="fetchContdata?.publishedAt.toString()">{{
              $dayjs(fetchContdata?.publishedAt).format("YYYY年MM月DD日")
            }}</time></span
          >
        </div>

        <div class="wrap-contents">
          <div class="main-contents">
            <div class="lead">
              <OrganismsCDNImg
                :id="fetchContdata?.id"
                :table-name="fetchContdata?.image?.tableName || ''"
                :file-name="fetchContdata?.image?.fileName || ''"
                width="640"
                height="427"
                alt=""
              />
              <div class="leadText" v-html="fetchContdata?.lead"></div>
            </div>

            <OrganismsTheContents />

            <OrganismsArticleSection
              v-for="item in fetchContdata?.articleSections"
              :id="item.id"
              :key="item.id"
              :title="item.title"
              :text="item.text"
            />
            <OrganismsMainCompareLink
              v-if="fetchContdata?.tags.find((el) => el.code === 'mansion')"
            />
            <OrganismsMainRequest v-else />
          </div>
          <OrganismsSideContents :sideCateDisplay="false" />
        </div>
      </article>
    </main>
  </div>
</template>

<style lang="scss" scoped>
/*---------------------------------------
.breadcrumbs
----------------------------------------*/
.breadcrumbs {
  border-top: 1px solid #dcdcdc;
  padding: 0.8em 4% 1.5em;
  font-size: 80%;
  line-height: 1.27;
  a {
    color: #2056a0;
  }
}

/*---------------------------------------
.button
----------------------------------------*/
.button-step1 {
  margin: 0 auto 3%;
  padding: 4.4% 0;
  border-radius: 6px;
  width: 100%;
  display: block;
  color: #fff;
  font-weight: bold;
  background-color: $colorBasicButton;
  box-shadow: 0px 2px 0px 0px rgba(0, 0, 0, 0.16);
  text-decoration: none;
  text-align: center;
  box-sizing: border-box;
  transition: 0.1s all;
  -webkit-tap-highlight-color: rgba(255, 255, 255, 0.3);

  > {
    span {
      margin: 0 0.1em 0 0;
      padding: 0.2em 0.2em 0.3em;
      display: inline-block;
      color: #f00;
      font-size: px(17);
      font-weight: 700;
      background-color: #ff0;
      line-height: 1;
      vertical-align: middle;
    }

    div {
      padding: 0 8% 0 0;
      display: inline-block;
      color: #fff;
      font-size: px(21);
      font-weight: 700;
      background: url(~/assets/images/arrow_orange_sp.png) no-repeat right
        center;
      background-size: 10% auto;
      vertical-align: middle;
      text-shadow: 2px 2px 0px rgba(241, 90, 36, 0.4);
    }
  }
}

.button-lease {
  margin: 0 auto 3%;
  padding: 3% 1%;
  border-radius: 4px;
  width: 93%;
  display: block;
  background: $colorSubButton url(~/assets/images/arrow_blue_sp.png) no-repeat
    94% center;
  background-size: 7% auto;
  text-align: center;
  box-shadow: 0px 2px 0px 0px rgba(0, 0, 0, 0.16);
  text-decoration: none;
  box-sizing: border-box;

  > div {
    margin: 0 auto;
    display: inline-block;
    color: $colorBlue;
    font-weight: 700;
  }

  span {
    margin: 0 0 0 -2%;
    display: inline-block;
    color: #333;
    font-size: px(9.9);
    font-weight: 400;
  }
}

.button-hojin {
  margin: 0 auto;
  padding: 3% 8%;
  border-radius: 45px;
  width: 93%;
  display: block;
  background: $colorSubButton url(~/assets/images/arrow_blue.png) no-repeat 94%
    center;
  background-size: 7% auto;
  text-align: left;
  box-shadow: 0px 2px 0px 0px rgba(0, 0, 0, 0.16);
  text-decoration: none;
  box-sizing: border-box;

  > div {
    margin: 0 auto;
    display: inline-block;
    color: $colorBlue;
    font-size: px(12);
    font-weight: 700;
  }
}

.button-linkTopics {
  margin: 0.8em 6% 0;
  padding: 0.5em;
  border: 1px solid #ccc;
  background-color: #fff;
  text-align: center;

  a {
    color: $colorBlue;
    display: block;
    font-size: px(11);

    &:hover {
      text-decoration: underline;
    }
  }
}

/*---------------------------------------
.request
----------------------------------------*/

:deep .request {
  margin: 6% 4% 10% !important;
  border: 4px solid $colorBlue;
  background-color: $colorBasicBackground;
}

.request-catch {
  margin: 0.3em auto 0.5em;
  width: 15.6em;
  display: block;
  color: #fff;
  font-size: px(18);
  font-weight: 700;
  text-align: center;
  position: relative;
  text-shadow: 2px 2px 0 rgba(30, 134, 198, 0.4);

  &::before,
  &::after {
    content: '';
    width: 1px;
    height: 80%;
    display: block;
    background-color: #fff;
    position: absolute;
    top: 15%;
  }

  &::before {
    left: -0.4em;
    transform: rotate(-45deg);
  }

  &::after {
    right: -0.4em;
    transform: rotate(45deg);
  }

  b {
    color: #fff800;
    font-weight: 700;
  }
}

.request-button {
  padding: 5% 4%;
  background-color: #fff;
  text-align: center;
}

/*---------------------------------------
.wrap-contents
----------------------------------------*/

.wrap-contents {
  padding: 0 0 14%;
  position: relative;
}

.pageTop {
  @include pageTop(sp);
}

/*---------------------------------------
.main-contents
----------------------------------------*/

.main-contents {
  padding: 0 0 10%;
}

h1 {
  padding: 1em 4%;
  font-size: px(19.9);
  font-weight: 700;
  background: url(~/assets/images/bg_pattern_02.gif) no-repeat 0 0;
  background-size: 100% 100%;
}

.update {
  padding: 0.5em 4% 0;
  color: #808080;
  font-size: px(11.4);

  span {
    margin: 0 1em 0 0;
  }
}

.lead {
  padding: 0 4%;

  :deep .leadText {
    padding: 1em 0 0;

    p {
      margin-bottom: 1em;
      font-size: px(12.9);
      line-height: 1.77;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}

.lead {
  img {
    padding: 2em 0 0;
  }

  p {
    padding: 1em 0 0;
    font-size: px(12.9);
    line-height: 1.77;
  }
}

/*---------------------------------------
.side-contents
----------------------------------------*/

.side-cont-new {
  padding: 0 0 8%;

  h2 {
    margin: 0 4%;
    padding: 0.5em;
    color: $colorBlue;
    background-color: $colorGray;
    text-align: center;
  }
}

.side-cont-list {
  margin: 0 4%;
  padding: 0 0.8em;
  border: 1px solid #dcdcdc;

  li {
    &:not(:last-of-type) {
      border-bottom: 1px solid #dcdcdc;
    }

    a {
      padding: 0.8em 0;
      display: block;
      color: #1b59b4;
      line-height: 1.53;
    }
  }

  img {
    float: left;
    width: 16%;
  }

  a > div {
    margin: 0 0 0 20%;

    time {
      display: block;
      color: #666;
      font-size: px(12.9);
    }

    span {
      height: 3em;
      display: block;
      font-weight: 700;
      overflow: hidden;
    }
  }
}

.side-request {
  background-color: #f0faff;
}

.side-request-catch {
  padding: 0.5em 0;
  color: #fff;
  font-size: px(19.4);
  background-color: $colorBasicBackground;
  text-align: center;

  b {
    color: #fff800;
  }

  br {
    display: none;
  }
}

.side-request-button {
  padding: 0 4% 5%;

  p {
    padding: 1em 0;
    font-size: px(12.9);
  }
}

@include media(pc) {
    /*---------------------------------------
  .breadcrumbs
  ----------------------------------------*/
  .breadcrumbs {
    border-top: none;
    margin: 0 auto;
    padding: 11px 0 34px;
    width: 960px;
  }
  /*---------------------------------------
.button
----------------------------------------*/

  .button-step1 {
    margin: 0 auto 21px;
    padding: 21px 0;
    width: 500px;
    display: inline-block;
    vertical-align: middle;
    box-shadow: 0px 4px 0px 0px rgba(0, 0, 0, 0.16);
  }

  .mainVisual-request .button-step1 {
    margin: 0;
  }

  .button-step1 {
    &:hover {
      background-color: #f78c35;
    }

    > {
      span {
        margin: 0 0.5em 0 0;
        padding: 0.2em 0.2em 0.3em;
        font-size: px(19.9);
      }

      div {
        padding: 0 8% 0 0;
        font-size: px(30);
        background: url(~/assets/images/arrow_orange.png) no-repeat right
          center;
        background-size: 28px 28px;
      }
    }
  }

  .button-lease {
    margin: 0;
    padding: 15px 30px;
    width: 432px;
    display: inline-block;
    background: $colorSubButton url(~/assets/images/arrow_blue.png) no-repeat
      90% center;
    background-size: 28px 28px;
    vertical-align: middle;
    box-shadow: 0px 4px 0px 0px rgba(0, 0, 0, 0.16);

    &:hover {
      background: #efefef url(~/assets/images/arrow_blue.png) no-repeat 90%
        center;
      background-size: 28px 28px;
    }

    > div {
      font-size: px(24);
    }

    span {
      font-size: px(12);
    }
  }

  .button-hojin {
    margin: 0 auto;
    padding: 13px 10px;
    width: 432px;
    display: flex;
    align-items: center;
    background: $colorSubButton url(~/assets/images/arrow_blue.png) no-repeat
      90% center;
    background-size: 20px 20px;
    vertical-align: middle;
    box-shadow: 0px 4px 0px 0px rgba(0, 0, 0, 0.16);

    &:hover {
      background: #efefef url(~/assets/images/arrow_blue.png) no-repeat 90%
        center;
      background-size: 20px 20px;
    }

    > div {
      font-size: px(16);
    }
  }

  .button-linkTopics {
    margin: 0.8em auto 0;
    padding: 0.5em;
    width: $pcWrapWidth;

    a {
      font-size: px(12.9);

      &:not(:first-child) {
        margin: 0.4em 0 0;
      }
    }
  }

  /*---------------------------------------
.request
----------------------------------------*/

  :deep .request {
    margin: 6% 0 10% !important;
    border: 6px solid $colorBlue;
    border-radius: 4px;
  }

  .request-catch {
    margin: 0.3em auto;
    width: 15.6em;
    font-size: px(27.9);

    &::before,
    &::after {
      content: '';
      width: 1px;
      height: 80%;
      display: block;
      background-color: #fff;
      position: absolute;
      top: 15%;
    }

    &::before {
      left: -0.4em;
      transform: rotate(-45deg);
    }

    &::after {
      right: -0.4em;
      transform: rotate(45deg);
    }

    b {
      color: #fff800;
      font-weight: 700;
    }
  }

  .request-button {
    padding: 26px 0 31px;
    background-color: #fff;
  }

  .request {
    .button-lease {
      margin: 0 auto 21px;
      background: $colorSubButton url(~/assets/images/arrow_blue.png) no-repeat
        94% center;
      background-size: 28px 28px;

      > div {
        font-size: px(24);
      }

      span {
        display: block;
        font-size: px(14);
      }
    }

    .button-hojin {
      background: $colorSubButton url(~/assets/images/arrow_blue.png) no-repeat
        94% center;
      background-size: 20px 20px;
    }
  }

  /*---------------------------------------
.wrap-contents
----------------------------------------*/

  .wrap-contents {
    margin: 0 auto;
    padding: 34px 0 32px;
    width: $pcWrapWidth;
    display: flex;
    justify-content: space-between;
  }

  .pageTop {
    @include pageTop(pc);
  }

  /*---------------------------------------
.main-contents
----------------------------------------*/

  .main-contents {
    padding: 0;
    width: $pcMainContentsWidth;
  }

  article {
    margin: 0 auto;
    width: $pcWrapWidth;
  }

  h1 {
    padding: 18px 18px 20px;
    font-size: px(31.9);
  }

  .update {
    padding: 10px 0 0;
    font-size: px(12.9);
  }
  .lead {
    padding: 0;

    :deep .leadText {
      padding: 1em 0 0;

      p {
        margin-bottom: 1em;
        font-size: px(15);
        line-height: 1.66;

        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }

  .lead img {
    padding: 0;
  }

  /*---------------------------------------
.side-contents
----------------------------------------*/

  .side-contents {
    width: $pcSideContentsWidth;
  }

  /*---------------------------------------
.side-cont-new
----------------------------------------*/

  .side-cont-new {
    padding: 0 0 20px;

    h2 {
      margin: 0;
      font-size: px(21.9);
    }
  }

  .side-cont-list {
    margin: 0;
    padding: 0 21px;
    border: 1px solid #dcdcdc;

    li {
      &:not(:last-of-type) {
        border-bottom: 1px solid #dcdcdc;
      }

      a {
        padding: 21px 0;
        line-height: 1.53;

        &:hover {
          opacity: 0.7;
        }
      }
    }

    img {
      width: 90px;
      height: 90px;
    }

    a > div {
      margin: 0 0 0 100px;

      time {
        font-size: px(15);
      }

      span {
        height: 4.6em;
      }
    }
  }

  .side-request-catch {
    padding: 10px 0;
    font-size: px(21.9);
    font-weight: 700;

    b {
      font-weight: 700;
    }

    br {
      display: block;
    }
  }

  .side-request-button {
    padding: 0 21px 18px;

    p {
      padding: 20px 0;
      font-size: px(15);
    }

    .button-step1 {
      margin: 0 auto 3%;
      padding: 10px 0;
      width: 100%;

      > {
        span {
          margin: 0 auto;
          padding: 0.2em 0.2em 0.2em;
          width: 2.8em;
          display: block;
          font-size: px(18.9);
        }

        div {
          padding: 0;
          font-size: px(21.9);
          background: none;
        }
      }
    }
  }
}
</style>
